// DsStatusChip — implementa a pílula de situação do canvas (DESIGN-SYSTEM.md §3.2):
//   `.chipsoft` em 100 dos 143 artboards, com `.cs-info` / `.cs-success` /
//   `.cs-warning` / `.cs-error` / `.cs-default` -> prop `tone`
//   `.chip` e `.modechip` -> tone="primary"
//   `.stchip` -> este componente + `sx={{ overflow:'hidden', textOverflow:'ellipsis' }}`
//   `.chipout` / `.co-*` (aposentadas pelos lotes UI-1) -> o tom equivalente
//
// Wrapper fino sobre <Label variant="soft">. A geometria do canvas passa a ser a do
// `Label` (§8-T5: `padding:4px 10px; borderRadius:999px; 11px/700; lineHeight:13px;
// height:auto; minWidth:0` -> altura resultante 21px), então este arquivo NÃO repete
// valor de geometria. Ele existe para (a) fechar a união de tons, (b) hospedar
// `mapStatus`, (c) impedir que a view volte a calcular cor na mão — matando o
// `darken(color,0.24) + hexAlpha(color,0.16)` de `dte-details.tsx:110-124` e o
// `map-status-cor.ts` de `malha-fiscal-parametrizacao`.
//
// A fórmula de cor do `Label` (`varAlpha(mainChannel, 0.16)` + `palette[color].dark`,
// `.light` no dark) já bate 1:1 com as `.cs-*` e não é reescrita aqui. A correção do
// `tone='default'` (grey[700]/grey[300] em vez de `text.secondary`) vive no tema (§8-T5).
//
// Única exceção escrita neste arquivo: `.chip`/`.modechip` sobem o alfa para 0.24 no
// dark (§3.2) — é o único tom que sobe o alfa, e isso não cabe no `Label`, que aplica
// 0.16 para as sete cores.
//
// ----------------------------------------------------------------------
// RODADA DE CONSOLIDAÇÃO DA CAMADA COMPARTILHADA
// DECISOES-PENDENTES.md §3.13 (**3 lotes**: L01/L02/L04 na escala de risco, L19 na
// NFS-e, L23 em `Finalizado`) e §1.14.
//
//  1. `DsTone` ganha `'secondary'`. §3.2 fechou a união em seis tons, mas o canvas
//     desenha `.st-secondary` (roxo `#8E33FF`) em `malha-parametrizacao`
//     (`Finalizado`) e em `malha-manutencao-periodos-excluidos` (`superusuario`) —
//     não é tom inventado, é tom que estava faltando. O `Label` do MUI já aceita
//     `color="secondary"` (`src/components/label/types.ts`), então a fórmula de cor
//     continua valendo sem uma linha de CSS nova.
//
//     ATENÇÃO (quebra de tipo conhecida, fora do alcance deste arquivo):
//     `src/modules/processo-eletronico/pages/detalhe.tsx:139` declara
//     `const COR_DO_TOM: Record<DsTone, string>` e passa a faltar a chave
//     `secondary`. É UMA linha (`secondary: 'secondary.main',`). Nenhum outro
//     arquivo do app usa `DsTone` em posição de chave — os demais 35 usos são
//     `Record<X, DsTone>` ou retorno de função, que aceitam a união maior.
//
//  2. `mapStatus` passa a conhecer os três vocabulários que caíam todos em
//     `'default'` (chip cinza) enquanto o artboard pintava:
//       - escala de risco `CRÍTICO/ALTO/MÉDIO/BAIXO` — cada view tinha a sua
//         `tomRisco()`/`tomDoRisco()` local (L01 F5, L02 6, L04 4);
//       - NFS-e `autorizada`/`emitida`/`sincronizada` — contornado por
//         `modules/nota-fiscal/utils/tom-situacao-nfse.ts` (L19 K3), que agora vira
//         repasse puro e pode ser apagado pelo dono daquele módulo;
//       - `Finalizado`, que caía em `success` pela regra `finaliz` e ficava com a
//         MESMA pílula verde de `Em Dia` na mesma grade (§1.14). Passa a `secondary`,
//         que é o que `malha-fiscal-parametrizacao/utils/map-status-cor.ts` já
//         escrevia à mão antes do canvas.
//
//     Mudança de cor visível, e de propósito, em duas telas: `malha-parametrizacao`
//     e `malha-execucao` (as únicas que chamam `mapStatus` com `Finalizado`).

import type { SystemStyleObject } from '@mui/system';
import type { Theme, SxProps } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import { Label } from 'src/components/label';

// ----------------------------------------------------------------------

export type DsTone =
  | 'default'
  | 'primary'
  /** `.st-secondary` roxo do canvas: `Finalizado` e `superusuario` (§1.14). */
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

export type DsStatusChipProps = {
  children: React.ReactNode;
  /** Padrão: 'default'. */
  tone?: DsTone;
  sx?: SxProps<Theme>;
};

// ----------------------------------------------------------------------

/** `.chip`/`.modechip` no dark: `rgba(0,167,111,0.24)` + `primary.light`. */
const sxPrimaryDark = (theme: Theme): SystemStyleObject<Theme> =>
  theme.applyStyles('dark', {
    backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.24),
    color: theme.vars.palette.primary.light,
  });

// ----------------------------------------------------------------------

export function DsStatusChip({ children, tone = 'default', sx }: DsStatusChipProps) {
  return (
    <Label
      variant="soft"
      color={tone}
      sx={[tone === 'primary' && sxPrimaryDark, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
    </Label>
  );
}

// ----------------------------------------------------------------------

/**
 * Traduz a situação crua do backend no tom do canvas.
 *
 * As chaves são dados (vêm do banco: `ordem_servico_situacao.situacao`,
 * `acao_fiscal`, `notificacao`, malha fiscal), não texto de interface — por isso
 * ficam literais e passam por normalização sem acento e sem caixa, em vez de
 * dependerem da grafia exata de cada módulo.
 *
 * Situação desconhecida cai em 'default', nunca em erro: o chip cinza é o
 * comportamento que as views já tinham no `?? { variant: 'outlined' }`.
 */
export function mapStatus(situacao: string): DsTone {
  const chave = normalizar(situacao);

  if (!chave) return 'default';

  const exato = SITUACOES[chave];
  if (exato) return exato;

  // A ordem importa: 'irregular' contém 'regular', e 'concluída sem resultado'
  // contém 'conclu'. O grupo mais específico é testado primeiro.
  const parcial = REGRAS.find(([termo]) => chave.includes(termo));

  return parcial ? parcial[1] : 'default';
}

/** Minúsculas, sem acento e sem espaço nas bordas. */
function normalizar(situacao: string): string {
  return situacao.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

/** Casamento exato — o que os `map-status-cor-*.ts` dos módulos já mapeavam. */
const SITUACOES: Record<string, DsTone> = {
  // neutros que uma regra parcial classificaria errado
  'nao iniciado': 'default',
  'nao iniciada': 'default',
  'concluida sem resultado': 'default',
  'concluido sem resultado': 'default',
  'nao se aplica': 'default',
  'n/a': 'default',
  // warning
  'concluida sem exame': 'warning',
  'parcialmente regular': 'warning',
  // success
  'em dia': 'success',
  'concluida com acao fiscal': 'success',
  // O radical 'autorizad' abaixo é de sucesso; a negativa tem de ser exata, senão
  // 'Não Autorizada' sairia verde.
  'nao autorizada': 'error',
  'nao autorizado': 'error',
  // info
  'aguardando aceite': 'info',
  prorrogado: 'info',
  prorrogada: 'info',
  // primary
  'em andamento': 'primary',
  // secondary — §1.14
  superusuario: 'secondary',
  // ------------------------------------------------------------------
  // Escala de risco / impacto / gravidade (§3.13). Fica SÓ no casamento exato: como
  // radical, 'alto', 'media' e 'baixa' casariam dentro de palavra ('mediante',
  // 'baixada'). Os três `tomRisco()` locais de L01/L02/L04 usam exatamente estes
  // quatro valores, com as duas flexões.
  // ------------------------------------------------------------------
  critico: 'error',
  critica: 'error',
  alto: 'warning',
  alta: 'warning',
  medio: 'info',
  media: 'info',
  baixo: 'success',
  baixa: 'success',
};

/**
 * Casamento por radical, em ordem de prioridade. Cobre as variações de gênero e as
 * conjugações que cada módulo escreve à sua maneira ('Cancelada'/'Cancelado',
 * 'Suspensa'/'Suspenso', 'Concluída'/'Concluído').
 */
const REGRAS: ReadonlyArray<readonly [string, DsTone]> = [
  ['sem resultado', 'default'],
  ['nao iniciad', 'default'],
  ['arquivad', 'default'],
  ['cancel', 'error'],
  ['rejeit', 'error'],
  ['indeferid', 'error'],
  ['irregular', 'error'],
  ['atras', 'error'],
  ['vencid', 'error'],
  ['bloquead', 'error'],
  ['omiss', 'error'],
  ['inadimpl', 'error'],
  ['invalid', 'error'],
  ['falha', 'error'],
  ['erro', 'error'],
  ['suspens', 'warning'],
  ['contencios', 'warning'],
  ['pendent', 'warning'],
  ['parcial', 'warning'],
  ['divergen', 'warning'],
  // §1.14: 'Finalizado' é roxo no canvas, e dividia a grade de `malha-parametrizacao`
  // com 'Em Dia', que é verde. Vinha antes desta rodada como 'success'.
  ['finaliz', 'secondary'],
  ['conclu', 'success'],
  ['regular', 'success'],
  // Vocabulário da NFS-e (§3.13): os artboards `nota-nfse` e `nf-integracao-dasd`
  // desenham os três em `.cs-success`. A negativa 'Não Autorizada' é interceptada
  // pelo casamento exato acima.
  ['autorizad', 'success'],
  ['emitid', 'success'],
  ['sincronizad', 'success'],
  ['deferid', 'success'],
  ['aprovad', 'success'],
  ['homologad', 'success'],
  ['entregue', 'success'],
  ['pago', 'success'],
  ['sucesso', 'success'],
  ['andamento', 'primary'],
  ['execucao', 'primary'],
  ['iniciad', 'primary'],
  ['aguard', 'info'],
  ['analise', 'info'],
  ['apuracao', 'info'],
  ['processa', 'info'],
  ['enviad', 'info'],
  ['recebid', 'info'],
  ['agendad', 'info'],
  ['abert', 'info'],
];
